/*
  Bridges the theme's own add-to-cart code (custom.js, and the offer blocks in
  vm_custom_product) to Dawn's cart drawer / cart notification, so adding a product
  paints the drawer instead of reloading /cart.

  Callers ask for the section list before posting to /cart/add.js, then hand the
  response back here. Anything unexpected returns false so the caller can fall back
  to its old redirect.
*/
(function () {
  function cartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  window.vmCartDrawerEnabled = function () {
    return !!document.querySelector('cart-drawer');
  };

  /*
    A custom element that has not been upgraded yet (its script failed, or ran late)
    still answers querySelector but has none of its methods. Upgrading on demand keeps
    that from silently turning every add into a redirect.
  */
  function upgraded(cart) {
    if (!cart) return null;
    if (typeof cart.renderContents !== 'function' && window.customElements) {
      try {
        window.customElements.upgrade(cart);
      } catch (error) {
        /* nothing to do */
      }
    }
    return cart;
  }

  // Section ids for the `sections` param on /cart/add.js. Null when there is no cart UI.
  window.vmCartSections = function () {
    var cart = upgraded(cartElement());
    if (!cart) return null;
    if (typeof cart.getSectionsToRender === 'function') {
      return cart.getSectionsToRender().map(function (section) {
        return section.id;
      });
    }
    // Same ids the two components ask for, in case neither has upgraded.
    console.warn('vm-cart-open: cart element not upgraded, using the default section list.');
    return cart.tagName === 'CART-DRAWER'
      ? ['cart-drawer', 'cart-icon-bubble']
      : ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'];
  };

  /*
    Applies a discount code without leaving the page. The offer buttons normally send
    the customer through /discount/CODE?redirect=..., which is a navigation; hitting the
    same URL with fetch sets the same cart cookie and lets us stay put.
    Always resolves: a failed discount should not block the add.
  */
  window.vmApplyDiscount = function (code) {
    if (!code) return Promise.resolve();
    return fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', {
      credentials: 'same-origin',
    })
      .then(function () {})
      .catch(function () {});
  };

  // Section Rendering API. Falls back to the home page path, which always renders.
  function fetchSections() {
    var ids = (window.vmCartSections() || []).join(',');
    var url = window.location.pathname + '?sections=' + encodeURIComponent(ids);
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('sections request failed');
        return res.json();
      })
      .catch(function () {
        return fetch('/?sections=' + encodeURIComponent(ids), { credentials: 'same-origin' }).then(function (res) {
          if (!res.ok) throw new Error('sections request failed');
          return res.json();
        });
      });
  }

  function paint(cart, response) {
    try {
      cart.renderContents(response);
    } catch (error) {
      console.error('Cart render failed.', error);
      window.location = '/cart';
      return;
    }

    /*
      renderContents only replaces the inner HTML, so `is-empty` set by Liquid on the
      <cart-drawer> host stays behind and keeps the drawer in its empty layout. We just
      added something, so the cart is not empty. Dawn does the same in product-form.js.
    */
    cart.classList.remove('is-empty');
    var inner = cart.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');
  }

  /*
    Only one element can carry id="cart-icon-bubble", and that is the one Shopify
    re-renders. The header shows a second cart icon on mobile, so copy the rendered
    contents across whenever the original changes. An observer catches every path:
    add to cart, quantity change in the drawer, remove.
  */
  function mirrorCartIcon() {
    var source = document.getElementById('cart-icon-bubble');
    if (!source) return;
    var links = document.querySelectorAll('.header_cart_icon');
    for (var i = 0; i < links.length; i++) {
      if (links[i] !== source) links[i].innerHTML = source.innerHTML;
    }
  }

  function watchCartIcon() {
    var source = document.getElementById('cart-icon-bubble');
    if (!source || !window.MutationObserver) return;
    new MutationObserver(mirrorCartIcon).observe(source, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCartIcon);
  } else {
    watchCartIcon();
  }

  window.vmRenderCart = function (response, trigger) {
    var cart = upgraded(cartElement());
    if (!cart) {
      console.warn('vm-cart-open: no cart-drawer or cart-notification on the page.');
      return false;
    }
    if (typeof cart.renderContents !== 'function') {
      console.warn('vm-cart-open: ' + cart.tagName.toLowerCase() + ' has no renderContents, its script did not run.');
      return false;
    }
    function finish(state) {
      // The notification picks its product markup by cart item key. A multi-item add
      // has no top level key, so use the first line it reports.
      if (!state.key && state.items && state.items.length) {
        state.key = state.items[0].key;
      }

      // custom.js hands us a jQuery object; the offer buttons hand us a plain element.
      var opener = trigger && trigger.jquery ? trigger[0] : trigger;
      if (opener && typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(opener);
      }

      // Added from the collection page quick add: close that modal first, then paint,
      // otherwise the modal sits on top of the drawer.
      var quickAdd = opener && opener.closest ? opener.closest('quick-add-modal') : null;
      if (!quickAdd) quickAdd = document.querySelector('quick-add-modal[open]');

      if (quickAdd && typeof quickAdd.hide === 'function') {
        document.body.addEventListener(
          'modalClosed',
          function () {
            setTimeout(function () {
              paint(cart, state);
            });
          },
          { once: true }
        );
        quickAdd.hide(true);
      } else {
        paint(cart, state);
      }
    }

    if (response && response.sections) {
      finish(response);
      return true;
    }

    /*
      Normally /cart/add.js hands back the rendered sections with the response. When it
      does not (a proxying app, a request that could not ask for them), fetch them from
      the Section Rendering API rather than giving up and redirecting.
    */
    console.warn('vm-cart-open: no sections in the add response, fetching them separately.');
    fetchSections()
      .then(function (sections) {
        finish({ sections: sections, key: response && response.key, items: response && response.items });
      })
      .catch(function (error) {
        console.error('vm-cart-open: could not fetch the cart sections.', error);
        window.location = '/cart';
      });
    return true;
  };
})();
